import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { GetPublicCompanyAPI, GetPublicAvailableSlotsRangeAPI } from '../../api/PublicScheduleAPI';
import './PublicSchedulePage.css';

// ── Componentes base ──────────────────────────────────────────────────────────
import PublicScheduleLayout from './components/PublicScheduleLayout';
import PublicScheduleHeader from './components/PublicScheduleHeader';
import PublicScheduleStepper from './components/PublicScheduleStepper';
import TrustFooter from './components/TrustFooter';
import IntroScreen from './components/IntroScreen';

// ── Steps ─────────────────────────────────────────────────────────────────────
import StepProfessional from './steps/StepProfessional';
import StepProcedure from './steps/StepProcedure';
import StepDate from './steps/StepDate';
import StepTime from './steps/StepTime';
import StepConfirm from './steps/StepConfirm';

// ── Helpers ───────────────────────────────────────────────────────────────────

const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatDatePT = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
};

const buildWhatsAppMessage = ({ professional, procedures, date, time, patientName, patientPhone }) => {
    const lines = [
        `Olá! Gostaria de solicitar um agendamento:`,
        ``,
        `*Profissional:* ${professional}`,
        `*${procedures.length > 1 ? 'Serviços' : 'Serviço'}:* ${procedures.join(' + ')}`,
        `*Data:* ${formatDatePT(date)}`,
        `*Horário:* ${time}`,
        ``,
        `*Nome:* ${patientName}`,
        `*Telefone:* ${patientPhone}`,
    ];
    return encodeURIComponent(lines.join('\n'));
};

/**
 * Converte duração do procedimento (formato HH:MM:SS, HH:MM ou número) para minutos.
 */
const parseDurationMinutes = (duration) => {
    if (!duration) return 0;
    const dur = String(duration).trim();
    const parts = dur.split(':');
    if (parts.length >= 2) {
        return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    }
    return parseInt(dur, 10) || 0;
};

/** Soma das durações dos serviços marcados, em minutos. */
const sumDurationMinutes = (procedures) =>
    procedures.reduce((sum, proc) => sum + parseDurationMinutes(proc.duration), 0);

/**
 * Retorna start_date e end_date para um mês (YYYY-MM-DD).
 * start_date é o dia 1 do mês (ou hoje, se for o mês atual).
 * end_date é o último dia do mês.
 */
const getMonthRange = (year, month) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);

    const start = (firstOfMonth < today) ? today : firstOfMonth;

    return {
        startDate: toDateStr(start),
        endDate: toDateStr(lastOfMonth),
    };
};

// ── Constante de step "intro" ─────────────────────────────────────────────────
const STEP_INTRO = -1;

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PublicSchedulePage() {
    const { companySlug, resourceSlug } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [company, setCompany] = useState(null);

    // step: -1 = intro, 0-4 = wizard, 'done' = sucesso
    const [step, setStep] = useState(STEP_INTRO);
    const [selectedProfessional, setSelectedProfessional] = useState(null);
    // Serviços marcados, na ordem em que a pessoa marcou
    const [selectedProcedures, setSelectedProcedures] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [patientName, setPatientName] = useState('');
    const [patientPhone, setPatientPhone] = useState('');

    // Range data: { "YYYY-MM-DD": { hasAvailability, availableCount, slots } }
    const [rangeData, setRangeData] = useState({});
    const [rangeLoading, setRangeLoading] = useState(false);
    // Track which months have been loaded: "YYYY-MM" => true
    const [loadedMonths, setLoadedMonths] = useState({});

    useEffect(() => {
        if (!companySlug) { setError('Link inválido.'); setLoading(false); return; }
        GetPublicCompanyAPI(companySlug, (res) => {
            setLoading(false);
            if (res.success) {
                setCompany(res.company);
                if (resourceSlug && res.company?.professionals) {
                    const fullSlug = companySlug + '/' + resourceSlug;
                    const prof = res.company.professionals.find(
                        p => p.slug === fullSlug || p.slug === resourceSlug
                    );
                    if (prof) {
                        setSelectedProfessional(prof);
                        setStep(1); // pula intro e step 0
                    } else {
                        setStep(STEP_INTRO);
                    }
                } else {
                    setStep(STEP_INTRO);
                }
            } else {
                setError(res.error || 'Empresa não encontrada.');
            }
        });
    }, [companySlug, resourceSlug]);

    /**
     * Carrega a disponibilidade de um mês inteiro via range API.
     */
    const loadMonthRange = useCallback((year, month) => {
        if (!selectedProfessional || !companySlug) return;

        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
        // Já carregado? Não recarrega
        if (loadedMonths[monthKey]) return;

        const { startDate, endDate } = getMonthRange(year, month);
        // Se start > end (mês inteiramente no passado), não carrega
        if (startDate > endDate) return;

        const durationMinutes = sumDurationMinutes(selectedProcedures);

        setRangeLoading(true);
        GetPublicAvailableSlotsRangeAPI(
            companySlug,
            selectedProfessional.id,
            startDate,
            endDate,
            (res) => {
                setRangeLoading(false);
                if (res.success && res.dates) {
                    setRangeData(prev => ({ ...prev, ...res.dates }));
                    setLoadedMonths(prev => ({ ...prev, [monthKey]: true }));
                }
            },
            durationMinutes > 0 ? durationMinutes : undefined
        );
    }, [companySlug, selectedProfessional, selectedProcedures, loadedMonths]);

    /**
     * Quando o usuário seleciona uma data no calendário.
     * Os slots já estão no rangeData (cache).
     */
    const handleDateSelect = (date) => {
        setSelectedDate(date);
        setSelectedTime('');

        // Usar os slots do cache (rangeData)
        if (rangeData[date]?.slots) {
            setSlots(rangeData[date].slots);
        } else {
            setSlots([]);
        }

        // Avança direto para o step de horários
        setStep(3);
    };

    const handleConfirm = () => {
        if (company.whatsapp) {
            const msg = buildWhatsAppMessage({
                professional: selectedProfessional.name,
                procedures: selectedProcedures.map(proc => proc.name),
                date: selectedDate,
                time: selectedTime,
                patientName,
                patientPhone,
            });
            const phone = company.whatsapp.replace(/\D/g, '');
            const url = `https://wa.me/${phone}?text=${msg}`;
            window.open(url, '_blank');
        }
    };

    const handleReset = () => {
        setStep(STEP_INTRO);
        setSelectedProfessional(null);
        setSelectedProcedures([]);
        setSelectedDate('');
        setSelectedTime('');
        setPatientName('');
        setPatientPhone('');
        setSlots([]);
        setRangeData({});
        setLoadedMonths({});
    };

    // ── Loading ───────────────────────────────────────────────────────────────

    if (loading) return (
        <PublicScheduleLayout>
            <PublicScheduleHeader company={null} />
            <div className="ps-content">
                <div className="ps-page-loading">
                    <div className="ps-spinner" />
                    <span>Carregando...</span>
                </div>
            </div>
            <TrustFooter />
        </PublicScheduleLayout>
    );

    // ── Error ─────────────────────────────────────────────────────────────────

    if (error) return (
        <PublicScheduleLayout>
            <PublicScheduleHeader company={null} />
            <div className="ps-content">
                <div className="ps-page-error">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <p>{error}</p>
                </div>
            </div>
            <TrustFooter />
        </PublicScheduleLayout>
    );

    // ── Intro ─────────────────────────────────────────────────────────────────

    if (step === STEP_INTRO) return (
        <PublicScheduleLayout>
            <PublicScheduleHeader company={company} />
            <div className="ps-content">
                <IntroScreen onStart={() => setStep(0)} company={company} />
            </div>
            <TrustFooter />
        </PublicScheduleLayout>
    );

    // ── Wizard ────────────────────────────────────────────────────────────────

    return (
        <PublicScheduleLayout>
            <PublicScheduleHeader company={company} />
            <div className="ps-content">
                <PublicScheduleStepper current={step} />

                {step === 0 && (
                    <StepProfessional
                        professionals={company.professionals || []}
                        selected={selectedProfessional}
                        onSelect={(prof) => {
                            setSelectedProfessional(prof);
                            setSelectedProcedures([]);
                            // Limpar cache de range ao trocar profissional
                            setRangeData({});
                            setLoadedMonths({});
                        }}
                        onNext={() => setStep(1)}
                    />
                )}

                {step === 1 && (
                    <StepProcedure
                        procedures={selectedProfessional?.procedures || []}
                        selected={selectedProcedures}
                        onToggle={(proc) => {
                            setSelectedProcedures(prev => (
                                prev.some(p => p.id === proc.id)
                                    ? prev.filter(p => p.id !== proc.id)
                                    : [...prev, proc]
                            ));
                            // Limpar cache de range ao mudar os serviços (duração total diferente)
                            setRangeData({});
                            setLoadedMonths({});
                        }}
                        onNext={() => setStep(2)}
                        onBack={() => setStep(0)}
                    />
                )}

                {step === 2 && (
                    <StepDate
                        selectedDate={selectedDate}
                        onSelect={handleDateSelect}
                        onNext={() => setStep(3)}
                        onBack={() => setStep(1)}
                        availableDaysOfWeek={company?.availableDaysOfWeek || [1, 2, 3, 4, 5]}
                        rangeData={rangeData}
                        rangeLoading={rangeLoading}
                        onMonthChange={loadMonthRange}
                    />
                )}

                {step === 3 && (
                    <StepTime
                        date={selectedDate}
                        slots={slots}
                        slotsLoading={slotsLoading}
                        selectedTime={selectedTime}
                        onSelect={setSelectedTime}
                        onNext={() => setStep(4)}
                        onBack={() => setStep(2)}
                    />
                )}

                {step === 4 && (
                    <StepConfirm
                        professional={selectedProfessional}
                        procedures={selectedProcedures}
                        date={selectedDate}
                        time={selectedTime}
                        patientName={patientName}
                        patientPhone={patientPhone}
                        onNameChange={setPatientName}
                        onPhoneChange={setPatientPhone}
                        onBack={() => setStep(3)}
                        onConfirm={handleConfirm}
                    />
                )}
            </div>
            <TrustFooter />
        </PublicScheduleLayout>
    );
}
